import Image from 'next/image';

import { MissionIcon } from '@/images/icons/MissionIcon';
import { ValuesIcon } from '@/images/icons/ValuesIcon';
import { VisionIcon } from '@/images/icons/VisionIcon';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Container } from '../common/Container';
import { sanitizeAndDecode } from '@/util/sanitizer_and_decoder';
import { getTextColorForBackground } from '@/util/get_text_color';

const renderCard = (icon, title, description) => {
  const [expanded, setExpanded] = useState(false);
  const [contentHeight, setContentHeight] = useState('0px');
  const [iconColor, setIconColor] = useState('black');

  const textRef = useRef(null);
  const iconContainerRef = useRef(null);

  useEffect(() => {
    if (!iconContainerRef.current) return;

    const computedStyle = getComputedStyle(iconContainerRef.current);
    const bgColor = computedStyle.backgroundColor;
    const textColorResult = getTextColorForBackground(bgColor);

    setIconColor(textColorResult);
  }, [icon]);

  useEffect(() => {
    if (textRef.current) {
      setContentHeight(expanded ? `${textRef.current.scrollHeight}px` : '88px');
    }
  }, [expanded]);

  const toggleExpanded = () => {
    setExpanded(!expanded);
  };

  return (
    <div className="flex flex-col items-center justify-center w-full lg:justify-start md:w-auto min-h-[280px]">
      <div
        ref={iconContainerRef}
        className="h-[72px] w-[72px] rounded-[14px] bg-primary flex items-center justify-center"
      >
        {React.cloneElement(icon, { color: iconColor })}
      </div>

      <div>
        <h3 className="font-helvetica font-bold text-lg">{title}</h3>
      </div>

      <div className="max-w-[346px] min-h-[88px]">
        <div
          ref={textRef}
          style={{ maxHeight: contentHeight }}
          className={`w-full transition-all duration-500 ease-in-out overflow-hidden ${
            expanded ? 'h-auto' : 'max-h-[88px]'
          }`}
        >
          <p className="text-center font-helvetica font-light">{description}</p>
        </div>

        {textRef.current?.scrollHeight > 88 && (
          <button onClick={toggleExpanded} className="w-full mt-2 text-primary underline">
            {expanded ? 'Mostrar menos' : 'Mostrar mais'}
          </button>
        )}
      </div>
    </div>
  );
};

function AboutUs({ about, about_image, type }) {
  const containerRef = useRef(null);

  const [expanded, setExpanded] = useState(false);
  const textRef = useRef(null);
  const [initialHeight, setInitialHeight] = useState('0');
  const [contentHeight, setContentHeight] = useState('0px');
  const defaultSectionImagePath = '/images/default_about_us.svg';
  const [clientAbout, setClientAbout] = useState('');
  const [textColor, setTextColor] = useState('black');

  const handleLoadImage = (e) => {
    const imageHeight = e.target.clientHeight;
    setInitialHeight(`${imageHeight / 2}`);
  };

  useEffect(() => {
    if (!containerRef.current) return;

    const computedStyle = getComputedStyle(containerRef.current);
    const bgColor = computedStyle.backgroundColor;
    const textColorResult = getTextColorForBackground(bgColor);

    setTextColor(textColorResult);
  }, [type]);

  useEffect(() => {
    const formattedAbout = sanitizeAndDecode(about);
    setClientAbout(formattedAbout);
  }, [about]);

  useEffect(() => {
    if (textRef.current) {
      const fullContentHeight = textRef.current.scrollHeight;
      if (initialHeight !== '0px') {
        setContentHeight(expanded ? `${fullContentHeight}px` : `${initialHeight}px`);
      }
    }
  }, [expanded, initialHeight]);

  return (
    <div
      ref={containerRef}
      className={`flex flex-col w-full rounded-[28px] lg:flex-row lg:max-h-[688px] gap-[60px] ${type === 'light' ? 'bg-gray-300' : 'bg-primary'}`}
    >
      <div className="px-7 md:pl-7 py-[26px] mx-auto xl:min-w-[522px]">
        <Image
          width="510"
          height="636"
          className="rounded-[41px] h-[400px] sm:min-w-[300px] md:h-[600px] lg:h-[636px] object-cover"
          src={about_image?.url || defaultSectionImagePath}
          alt="imagem de apresentação"
          onLoad={handleLoadImage}
          unoptimized
        />
      </div>

      <div
        style={{ color: textColor }}
        className="flex pb-8 px-7 flex-col gap-[13px] w-full justify-center items-center lg:pr-[54px]"
      >
        <h2 className="font-montserrat text-2xl w-full text-center font-bold lg:text-left lg:text-[36px] 2xl:text-5xl lg:leading-[43.2px]">
          Sobre Nós
        </h2>

        <p
          className="pb-4 text-justify pr-2 list-item font-helvetica leading-[21.6px] font-light text-lg 2xl:text-2xl max-h-[484px] overflow-auto [&::-webkit-scrollbar]:w-2
            [&::-webkit-scrollbar-track]:rounded-full
          [&::-webkit-scrollbar-track]:bg-gray-100
            [&::-webkit-scrollbar-thumb]:rounded-full
          [&::-webkit-scrollbar-thumb]:bg-gray-300
          dark:[&::-webkit-scrollbar-track]:bg-neutral-700
          dark:[&::-webkit-scrollbar-thumb]:bg-neutral-500"
          dangerouslySetInnerHTML={{ __html: clientAbout }}
        />
      </div>
    </div>
  );
}

export function CTA(props) {
  const { mission, vision, values } = props;

  return (
    <Container>
      <div id="about" className={`flex flex-col h-auto gap-20 py-20 mx-auto w-full`}>
        <AboutUs {...props} />
        <div
          className={`${mission && vision && values ? 'lg:grid lg:grid-cols-3' : 'lg:flex-row justify-between xl:justify-center'} flex flex-col justify-center items-center flex-wrap gap-40`}
        >
          {props.mission && renderCard(<MissionIcon />, 'Missão', mission)}
          {props.vision && renderCard(<VisionIcon />, 'Visão', vision)}
          {props.values && renderCard(<ValuesIcon />, 'Valores', values)}
        </div>
      </div>
    </Container>
  );
}
